/**
 * Admin audit log read API. Phase 10 of the phone admin IVR console.
 *
 * Customer-scoped GET only — every entry has actor_kind +
 * before_state/after_state JSON snapshots so the portal can show what
 * changed without a separate diff API.
 */
import { apiFetch } from './client';

export type AuditActorKind = 'phone_admin' | 'portal_user' | 'system';

export interface AuditEntryRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  actor_kind: AuditActorKind;
  actor_id: string | null;
  actor_e164: string | null;
  actor_email: string | null;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  /**
   * Resolved human-readable ref number for target_kind + target_id
   * (e.g. "IVR-1042", "REC-0005"). NULL when target_kind is unknown
   * or the row has been deleted. Backend computes with a CASE
   * subquery; see backend/src/auditLog/service.ts listAuditEntries.
   */
  target_ref: string | null;
  before_state: unknown;
  after_state: unknown;
  details: unknown;
  created_at: string;
}

export interface ListAuditEntriesQuery {
  customer_id: string;
  action?: string;
  actor_kind?: AuditActorKind;
  target_kind?: string;
  target_id?: string;
  /** Inclusive lower bound (YYYY-MM-DD or ISO datetime). */
  from?: string;
  /** Exclusive upper bound (YYYY-MM-DD or ISO datetime). */
  to?: string;
  page?: number;
  page_size?: number;
}

export interface ListAuditEntriesResponse {
  rows: AuditEntryRow[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * Map of UUID (lowercased) → friendly ref ("REC-0042", "IVR-1042",
   * etc.) for every UUID found inside before_state/after_state/details
   * across the returned rows that resolves to a known entity. The
   * portal uses this to inline-render UUIDs in the JSON detail dump
   * so admins don't have to memorise UUIDs. Empty {} when no UUIDs
   * in scope resolved. See backend/src/auditLog/service.ts
   * resolveDisplayRefs.
   */
  display_refs: Record<string, string>;
}

export const adminAuditLog = {
  list: (q: ListAuditEntriesQuery) => {
    const sp = new URLSearchParams();
    sp.set('customer_id', q.customer_id);
    if (q.action) sp.set('action', q.action);
    if (q.actor_kind) sp.set('actor_kind', q.actor_kind);
    if (q.target_kind) sp.set('target_kind', q.target_kind);
    if (q.target_id) sp.set('target_id', q.target_id);
    if (q.from) sp.set('from', q.from);
    if (q.to) sp.set('to', q.to);
    if (typeof q.page === 'number') sp.set('page', String(q.page));
    if (typeof q.page_size === 'number') sp.set('page_size', String(q.page_size));
    return apiFetch<ListAuditEntriesResponse>(`/api/admin-audit-log?${sp}`);
  },
};
